import { NavLink } from "react-router-dom";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export interface SidebarNavItemProps {
  to: string;
  icon: LucideIcon;
  label: string;
  end?: boolean;
  collapsed?: boolean;
  badge?: string | number;
  onNavigate?: () => void;
}

export function SidebarNavItem({
  to,
  icon: Icon,
  label,
  end,
  collapsed,
  badge,
  onNavigate,
}: SidebarNavItemProps) {
  const link = (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-lg pl-3 pr-3 py-2 text-sm transition-all duration-200",
          "text-foreground/70 hover:text-foreground hover:[background-color:hsl(var(--glass-tint)/0.06)]",
          isActive &&
            "bg-gradient-to-r from-primary/[0.12] via-primary/[0.06] to-transparent text-foreground",
          collapsed && "justify-center px-0"
        )
      }
      aria-label={label}
    >
      {({ isActive }) => (
        <>
          {/* Editorial gold left rail when active */}
          {isActive && !collapsed && (
            <span
              aria-hidden="true"
              className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-[var(--gradient-gold-foil)] shadow-[0_0_8px_hsl(var(--primary)/0.5)]"
            />
          )}
          {isActive && collapsed && (
            <span
              aria-hidden="true"
              className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r-full bg-[var(--gradient-gold-foil)]"
            />
          )}
          <Icon
            className={cn(
              "h-[18px] w-[18px] flex-shrink-0 transition-colors",
              isActive ? "text-primary" : "text-foreground/55 group-hover:text-foreground"
            )}
            aria-hidden="true"
          />
          {!collapsed && (
            <span
              className={cn(
                "flex-1 truncate transition-colors",
                isActive ? "font-semibold" : "font-medium"
              )}
            >
              {label}
            </span>
          )}
          {!collapsed && badge && (
            <span className="ml-auto rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-semibold text-primary">
              {badge}
            </span>
          )}
          {collapsed && badge && (
            <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary" />
          )}
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return link;
}
